import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Compass, HeartHandshake, ShieldCheck, Sparkles, Globe2, Zap } from "lucide-react";
import heroImg from "@/assets/about-hero.webp";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Spayzo — Spaces by the hour, made simple" },
      { name: "description", content: "Spayzo is the marketplace for booking inspiring spaces by the hour. Meet the team and the mission behind it." },
      { property: "og:title", content: "About Spayzo" },
      { property: "og:description", content: "The team and mission behind the marketplace for hourly spaces." },
      { property: "og:image", content: heroImg },
    ],
    links: [{ rel: "canonical", href: "https://spayzo.lovable.app/about" }],
  }),
  component: AboutPage,
});

const valueKeys = [
  { key: "hospitality", icon: HeartHandshake },
  { key: "trust", icon: ShieldCheck },
  { key: "fast", icon: Zap },
  { key: "local", icon: Globe2 },
] as const;

const teamKeys = [
  { name: "Lina Park", role: "ceo", initials: "LP" },
  { name: "Marcus Adelyi", role: "cto", initials: "MA" },
  { name: "Yuki Tanaka", role: "design", initials: "YT" },
  { name: "Noor Haddad", role: "ops", initials: "NH" },
] as const;

const statKeys = [
  { value: "12k+", key: "listed" },
  { value: "84", key: "cities" },
  { value: "240k", key: "hours" },
  { value: "4.9", key: "rating" },
] as const;

function AboutPage() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />

      <main className="flex-1">
        <section className="relative overflow-hidden">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:py-24">
            <div className="lg:col-span-6 lg:pt-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                {t("about.badge")}
              </div>
              <h1 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                {t("about.titleA")}
                <span className="block text-muted-foreground">{t("about.titleB")}</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
                {t("about.intro")}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full">
                  <Link to="/discover">{t("about.explore")}</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full">
                  <Link to="/contact">{t("about.talk")}</Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-tr from-primary/15 via-transparent to-accent/30 blur-2xl" />
                <img
                  src={heroImg}
                  alt={t("about.heroAlt")}
                  width={1600}
                  height={1024}
                  fetchPriority="high"
                  decoding="async"
                  className="aspect-[4/3] w-full rounded-3xl object-cover shadow-soft ring-1 ring-border"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-surface">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-10 sm:px-6 lg:grid-cols-4 lg:py-12">
            {statKeys.map((s) => (
              <div key={s.key}>
                <div className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{s.value}</div>
                <div className="mt-1 text-sm text-muted-foreground">{t(`about.stats.${s.key}`)}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="rounded-3xl border border-border bg-card p-8 shadow-soft sm:p-10">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Compass className="h-5 w-5" />
              </div>
              <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight">{t("about.missionTitle")}</h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">{t("about.missionBody")}</p>
            </div>

            <div className="rounded-3xl border border-border bg-card p-8 shadow-soft sm:p-10">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Sparkles className="h-5 w-5" />
              </div>
              <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight">{t("about.visionTitle")}</h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">{t("about.visionBody")}</p>
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-surface">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("about.valuesEyebrow")}</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("about.valuesTitle")}</h2>
            </div>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {valueKeys.map((v) => (
                <div key={v.key} className="group rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-0.5 hover:shadow-soft">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <v.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold">{t(`about.values.${v.key}.title`)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`about.values.${v.key}.body`)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
          <div className="flex items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("about.teamEyebrow")}</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("about.teamTitle")}</h2>
            </div>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {teamKeys.map((m) => (
              <div key={m.name} className="rounded-2xl border border-border bg-card p-6 text-center">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-accent text-xl font-semibold text-foreground">
                  {m.initials}
                </div>
                <div className="mt-5 font-display text-base font-semibold">{m.name}</div>
                <div className="mt-1 text-sm text-muted-foreground">{t(`about.team.${m.role}`)}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6">
          <div className="overflow-hidden rounded-3xl bg-foreground px-8 py-14 text-background sm:px-12 sm:py-20">
            <div className="grid items-center gap-8 lg:grid-cols-2">
              <div>
                <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("about.ctaTitle")}</h2>
                <p className="mt-4 max-w-lg text-background/70">{t("about.ctaBody")}</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Button asChild size="lg" variant="secondary" className="rounded-full">
                  <Link to="/discover">{t("about.ctaFind")}</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full border-background/30 bg-transparent text-background hover:bg-background hover:text-foreground">
                  <Link to="/contact">{t("about.ctaContact")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
